import React from 'react';
import InlineEdit from '@atlaskit/inline-edit';
import Textfield from '@atlaskit/textfield';
import { ErrorMessage } from '@atlaskit/form';
import { Flex, Stack, xcss } from '@atlaskit/primitives';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import Select, { ValueType } from '@atlaskit/select';

interface OptionType {
  label: string;
  value: string;
}

const readViewBorderStyles = xcss({
  width: '112px',
  height: '32px',
  border: '1px solid',
  borderColor: 'color.border.input',
  borderRadius: 'border.radius.050',
  padding: 'space.050',
});

export const ActiveUsersCell = ({
  activeUsers,
  setActiveUsers,
  isUserCountLimited1To99 = false,
  quantityTiers,
  selectedQuantityTier,
}: {
  activeUsers: string;
  setActiveUsers: (_activeUsers: string) => void;
  isUserCountLimited1To99?: boolean;
  quantityTiers?: string[];
  selectedQuantityTier?: string;
}) => {
  const isQuantityTierSelection = quantityTiers && quantityTiers?.length > 0;

  const [quantityTierValue, setQuantityTierValue] = React.useState<ValueType<OptionType, true>>([]);

  React.useEffect(() => {
    if (selectedQuantityTier) {
      setQuantityTierValue([{ label: selectedQuantityTier, value: selectedQuantityTier }]);
    }
  }, [quantityTiers, selectedQuantityTier]);

  const handleChange = (value: string) => {
    setActiveUsers(value?.trim());
  };

  const selectOptions: OptionType[] = React.useMemo(() => {
    if (!isQuantityTierSelection) {
      return [];
    }
    const selectPlanOptions = quantityTiers?.map((quantityTier) => ({
      label: quantityTier,
      value: quantityTier,
    }));
    return selectPlanOptions;
  }, [quantityTiers]);

  const validate = (value: string) => {
    const usersCount = value?.trim();
    if (!usersCount || isNaN(Number(usersCount)) || Number(usersCount) < 1) {
      return 'Invalid active users';
    }
    if (isUserCountLimited1To99 && Number(usersCount) > 99) {
      return 'Enter between 1-99';
    }
    return undefined;
  };

  return (
    <div>
      <InlineEdit<any>
        defaultValue={isQuantityTierSelection ? quantityTierValue : activeUsers}
        editView={({ errorMessage, ...fieldProps }) => {
          if (isQuantityTierSelection) {
            return (
              <Select
                {...fieldProps}
                options={selectOptions}
                onChange={(value) => {
                  if (value) {
                    setQuantityTierValue([value]);
                  }
                }}
                menuPortalTarget={document.body}
                autoFocus
                openMenuOnFocus
                testId="select-quantity-tier-cell"
              />
            );
          }
          return (
            <>
              <Textfield {...fieldProps} autoFocus testId="edit-active-user-cell" />
              {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            </>
          );
        }}
        readView={() => {
          return (
            <Flex
              testId="read-active-user-cell"
              alignItems="center"
              {...(!isQuantityTierSelection ? { xcss: readViewBorderStyles } : {})}
            >
              {isQuantityTierSelection ? (
                <>
                  <Stack>{selectedQuantityTier}</Stack>
                  <ChevronDownIcon label="More" size="medium" />
                </>
              ) : (
                activeUsers
              )}
            </Flex>
          );
        }}
        onConfirm={handleChange}
        hideActionButtons
        {...(!isQuantityTierSelection ? { validate: validate } : {})}
      />
    </div>
  );
};
